import type { Persona } from "@/types/persona";
import heroImage from "@/assets/personas/psychologist-real.jpg";

export const psychologists: Persona = {
  slug: "psychologists-mentors",
  profession: { hu: "pszichológus", en: "psychologist" },
  professionPlural: {
    hu: "pszichológusok és mentorok",
    en: "psychologists and mentors",
  },
  professionGenitive: { hu: "pszichológusként", en: "as a psychologist" },
  heroH1: {
    hu: "Fókuszálj a kliensre, az adminisztrációt pedig bízd ránk.",
    en: "Focus on your client, leave the admin to us.",
  },
  heroSub: {
    hu: "Teljes körű AI asszisztens pszichológusoknak és mentoroknak: saját weboldal és blog, online + személyes időpont, csomagok és bérletek, diszkrét emlékeztetők, ügyfél-CRM, e-mail automatizáció és bevétel-analitika — egy helyen.",
    en: "A full AI assistant for psychologists and mentors: personal website and blog, online + in-person booking, packages and passes, discreet reminders, client CRM, email automations and revenue analytics — all in one place.",
  },
  heroImage,
  heroImageAlt: "Psychologist in a professional office",
  chatExamplePrompt: {
    hu: "Foglalj 50 perc konzultációt, online, Mária",
    en: "Book a 50-minute online consultation for Mária",
  },
  chatExampleResponse: {
    hu: "Bejegyeztem szerda 16:00, 50 perc online ülés. Máriának küldtem visszaigazolást és a titkosított Google Meet linket.",
    en: "Booked Wednesday 4 PM, 50-minute online session. Confirmation and encrypted Google Meet link sent to Mária.",
  },
  accentColor: "slate",

  defaultMode: "solo",
  teamSize: { min: 1, max: 8 },
  soloLabel: { hu: "Szóló pszichológus", en: "Solo psychologist" },
  teamLabel: {
    hu: "Pszichológiai rendelő csapattal",
    en: "Psychology practice with a team",
  },

  soloDayInLife: [
    {
      time: "08:30",
      what: {
        hu: "Reggeli felkészülés. Az AI riportot küld: 1 új lead diszkrét kérdőívvel, 4 ülés ma.",
        en: "Morning prep. AI report: 1 new lead with discreet questionnaire, 4 sessions today.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "50 perc terápiás ülés Annával. A titkosított jegyzeteid előtte előkészítve.",
        en: "50-minute therapy session with Anna. Your encrypted notes are ready beforehand.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Új kliens előzetes kérdőívet tölt ki. Az AI rendszerezi és továbbítja, és bevezető időpontot javasol.",
        en: "New client fills out an intake form. AI organizes and forwards it, suggests an intro slot.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Online ülés Google Meeten. A link minden alkalomra egyedi, jelszavas.",
        en: "Online session on Google Meet. Each link is unique and password-protected.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Napzárás: 5 ülés, 95 000 Ft bevétel, jegyzetek titkosítva mentve.",
        en: "End of day: 5 sessions, 95,000 HUF revenue, notes saved encrypted.",
      },
    },
  ],
  teamDayInLife: [
    {
      time: "08:00",
      what: {
        hu: "Rendelő dashboard: 4 pszichológusod 18 ülést tart ma. Mindenki saját nézettel, kliens-adatok elkülönítve.",
        en: "Practice dashboard: your 4 psychologists run 18 sessions today. Each has their own view, client data is siloed.",
      },
    },
    {
      time: "10:00",
      what: {
        hu: "Új kliens pánikrohamra szakosodott terapeutát keres: az AI Évához osztja.",
        en: "New client looking for a panic-attack specialist: AI routes to Éva.",
      },
    },
    {
      time: "13:00",
      what: {
        hu: "Egy pszichológus beteg. Az AI értesíti az érintett klienseket, felajánl új időpontot vagy másik terapeutát.",
        en: "A psychologist is sick. AI notifies affected clients, offers new times or another therapist.",
      },
    },
    {
      time: "16:00",
      what: {
        hu: "Heti riport: ülés-szám, kliens-elégedettség, várólista hossza. Bizalmas adatok nem jelennek meg.",
        en: "Weekly report: session count, client satisfaction, waitlist length. Confidential data is not shown.",
      },
    },
    {
      time: "19:00",
      what: {
        hu: "Hónap vége: bevétel terapeutánként, jutalék-elszámolás.",
        en: "Month-end: revenue per therapist, commission settlement.",
      },
    },
    {
      time: "22:00",
      what: {
        hu: "Számlák kiállítva, könyvelő-export kész, GDPR-napló frissítve.",
        en: "Invoices issued, accountant export ready, GDPR log updated.",
      },
    },
  ],

  soloFeatures: [
    {
      icon: "Calendar",
      title: {
        hu: "Diszkrét foglalási felület",
        en: "Discreet booking interface",
      },
      body: {
        hu: "Nem a teljes naptár látszik, csak a szabad slot-ok. Jelszavas védelem opció.",
        en: "The full calendar isn't visible: only free slots. Password protection option.",
      },
    },
    {
      icon: "Bot",
      title: {
        hu: "Az AI fogadja az első megkeresést",
        en: "AI handles first contact",
      },
      body: {
        hu: "Új érdeklődő: az AI elküldi a kérdőívet, válaszol az alapokról, és csak komoly leadet ad át.",
        en: "New lead: AI sends the questionnaire, answers basics, only passes serious leads to you.",
      },
    },
    {
      icon: "ShieldCheck",
      title: { hu: "Titkosított jegyzetek", en: "Encrypted notes" },
      body: {
        hu: "Minden ülés-jegyzet end-to-end titkosítva. Csak te férsz hozzá, EU-szervereken tárolva.",
        en: "Every session note is end-to-end encrypted. Only you can access them, stored on EU servers.",
      },
    },
    {
      icon: "Wallet",
      title: {
        hu: "Online + helyszíni fizetés",
        en: "Online + in-person payments",
      },
      body: {
        hu: "Beépített online kártya, Apple/Google Pay foglaláskor; helyszínen készpénz vagy POS is rögzíthető: minden tranzakció egy felületen, automata számlával.",
        en: "Built-in online card, Apple/Google Pay at booking; in-person cash or POS also tracked: every transaction in one place with auto-invoicing.",
      },
    },
  ],
  teamFeatures: [
    {
      icon: "Users",
      title: {
        hu: "Korlátlan pszichológus, egy ár",
        en: "Unlimited psychologists, one price",
      },
      body: {
        hu: "1 vagy 8 fő: ugyanaz a havidíj. Mindenki saját naptárral és kliens-bázissal.",
        en: "1 or 8: same monthly fee. Each with their own calendar and clients.",
      },
    },
    {
      icon: "ShieldCheck",
      title: {
        hu: "Szigorú jogosultság-kezelés",
        en: "Strict role-based access",
      },
      body: {
        hu: "Egészségügyi adatokhoz CSAK az érintett pszichológus fér hozzá. Te tulajdonosként az aggregált statisztikát látod, kliens-adatokat nem.",
        en: "Only the assigned psychologist accesses medical data. As owner you see aggregated stats: not client data.",
      },
    },
    {
      icon: "Sparkles",
      title: { hu: "AI elosztja a klienseket", en: "AI assigns clients" },
      body: {
        hu: "Új lead: az AI specialitás (pánik, gyermek, családterápia) alapján a megfelelő pszichológushoz osztja.",
        en: "New lead: AI assigns by specialty (panic, child, family therapy) to the right psychologist.",
      },
    },
    {
      icon: "TrendingUp",
      title: {
        hu: "Automatikus bevétel és jutalék",
        en: "Automatic revenue and commission",
      },
      body: {
        hu: "Ülés-szám terapeutánként, bevételi riport exportálható, jutalék-elszámolás automatikus.",
        en: "Session count per therapist, exportable revenue report, automatic commission settlement.",
      },
    },
  ],

  valueProps: [
    {
      icon: "Calendar",
      title: { hu: "Online és személyes időpont", en: "Online and in-person sessions" },
      body: {
        hu: "Egy naptár mindkettőhöz, csomag és bérlet online fizetéssel, automata Google Meet link és diszkrét emlékeztetők.",
        en: "One calendar for both, packages and passes with online payment, auto Google Meet link and discreet reminders.",
      },
    },
    {
      icon: "BookOpen",
      title: { hu: "Blog és SEO tartalom", en: "Blog and SEO content" },
      body: {
        hu: "Az AI szakmai blogcikkeket, edukációs bejegyzéseket és GYIK-oldalakat ír a szakterületedről: a Google-ben megtalálnak az új ügyfelek.",
        en: "AI writes professional blog articles, educational posts and FAQ pages on your field: new clients find you on Google.",
      },
    },
    {
      icon: "Mail",
      title: { hu: "CRM és e-mail sorozat", en: "CRM and email sequences" },
      body: {
        hu: "Titkosított ügyfél-kartoték jegyzetekkel, ülésekhez kötött feladatokkal. Ülés utáni köszönő e-mail, edukációs sorozat, csomag-lejáráskor emlékeztető: az AI küldi.",
        en: "Encrypted client records with notes and session-linked tasks. Post-session thank-you, educational sequences, package-expiry reminders: AI sends them.",
      },
    },
    {
      icon: "BarChart3",
      title: { hu: "Bevétel és kihasználtság", en: "Revenue and utilisation" },
      body: {
        hu: "Heti riport: kihasználtság, csomag-bevétel, honnan érkeznek az új ügyfelek, mely tartalom hoz foglalást — döntési javaslatokkal.",
        en: "Weekly report: utilisation, package revenue, where new clients come from, which content drives bookings — with actionable suggestions.",
      },
    },
  ],

  aiScope: [
    {
      categoryKey: "content",
      examples: [
        { hu: "Edukációs karusszel a stresszről, kiégésről, szorongásról", en: "Educational carousel on stress, burnout, anxiety" },
        { hu: "Idézet-posztok saját szakmai hangoddal", en: "Quote posts written in your professional voice" },
        { hu: "Blog-teaser LinkedIn-re és Facebook-ra rövid összefoglalóval", en: "Blog teasers for LinkedIn and Facebook with a short summary" },
      ],
    },
    {
      categoryKey: "seo",
      examples: [
        { hu: "GYIK-oldal: 'Hogyan zajlik egy terápiás folyamat?'", en: "FAQ page: 'How does a therapy process work?'" },
        { hu: "Blogcikk-sorozat módszerenként (CBT, sématerápia, coaching)", en: "Blog series per method (CBT, schema therapy, coaching)" },
        { hu: "Külön szolgáltatás-oldal: párterápia, mentorálás, csoport", en: "Dedicated service pages: couples, mentoring, group" },
      ],
    },
    {
      categoryKey: "email",
      examples: [
        { hu: "Ülés utáni köszönő e-mail reflexiós kérdésekkel", en: "Post-session thank-you email with reflection prompts" },
        { hu: "Csomag-hosszabbítási emlékeztető 2 üléssel a lejárat előtt", en: "Package renewal reminder 2 sessions before the end" },
        { hu: "GDPR-barát, téma-mentes emlékeztető sablonok", en: "GDPR-safe, topic-free reminder templates" },
      ],
    },
    {
      categoryKey: "crm",
      examples: [
        { hu: "Titkosított ügyfél-kartoték, ülésekhez kötött privát jegyzet", en: "Encrypted client records, private notes tied to sessions" },
        { hu: "Csomag-státusz: hátralévő ülések, következő időpont", en: "Package status: remaining sessions, next appointment" },
        { hu: "Beérkezési forrás követése (ajánlás, Google, LinkedIn)", en: "Intake source tracking (referral, Google, LinkedIn)" },
      ],
    },
  ],



  objections: [
    {
      question: {
        hu: "Bizalmas adatokat kezelek: biztonságos a rendszer?",
        en: "I handle confidential data: is the system secure?",
      },
      answer: {
        hu: "Igen. EU-szerverek, GDPR-megfelelőség, end-to-end titkosítás minden ülés-jegyzetnél. Az adatok SOHA nem kerülnek 3. félhez, AI training-re sem. Jogosultság-kezeléssel csak te férsz hozzá a saját kliens-adataidhoz.",
        en: "Yes. EU servers, GDPR-compliant, end-to-end encryption on every session note. Data is NEVER shared with 3rd parties, never used for AI training. Role-based access: only you see your client data.",
      },
    },
    {
      question: {
        hu: "Rendelőben dolgozunk több pszichológussal: hogy kezeli a csapatot?",
        en: "We work in a practice with multiple psychologists: how does it handle the team?",
      },
      answer: {
        hu: "Minden pszichológusnak saját naptára, kliens-bázisa, titkosított jegyzetei vannak. Te tulajdonosként az AGGREGÁLT statisztikát látod (bevétel, ülés-szám), de a kliens-adatokhoz NEM férsz hozzá. Csak az érintett pszichológus láthatja a saját klienseit.",
        en: "Each psychologist has their own calendar, clients and encrypted notes. As owner you see AGGREGATED stats (revenue, session count) but do NOT access client data. Only the assigned psychologist sees their clients.",
      },
    },
    {
      question: {
        hu: "A kliens-kapcsolat különösen érzékeny: kell az automatizáció?",
        en: "The client relationship is especially sensitive: do I need automation?",
      },
      answer: {
        hu: "Az automata csak a logisztikát végzi (foglalás, emlékeztető, számla), a kliens-kapcsolatot NEM. Sőt: kevesebb adminisztrációt jelent, így 100%-ban a klienssel foglalkozhatsz az ülésben.",
        en: "Automation only handles logistics (booking, reminders, invoicing): NOT the relationship. In fact, less admin means you can focus 100% on the client during the session.",
      },
    },
    {
      question: {
        hu: "Az online ülésekhez biztonságos linkek kellenek",
        en: "Online sessions need secure links",
      },
      answer: {
        hu: "Minden ülésre egyedi Google Meet link generálódik, jelszavas védelemmel. A kliens csak a foglalt időpontra tud belépni. A felvétel funkció ALAPÉRTELMEZETTEN tiltva van.",
        en: "Every session gets a unique, password-protected Google Meet link. The client can only join at the booked time. Recording is DISABLED by default.",
      },
    },
  ],

  monetization: [
    {
      icon: "Heart",
      title: { hu: "Egyéni terápia", en: "Individual therapy" },
      examples: [
        { hu: "50 perc terápiás ülés", en: "50-minute therapy session" },
        { hu: "Bevezető konzultáció", en: "Intro consultation" },
        { hu: "Online ülés", en: "Online session" },
      ],
      priceHint: {
        hu: "Példa: 60 ülés × 22 000 Ft = 1 320 000 Ft / hó",
        en: "Example: 60 sessions × 22,000 HUF = 1,320,000 HUF / month",
      },
    },
    {
      icon: "Users",
      title: { hu: "Pár- és családterápia", en: "Couples and family therapy" },
      examples: [
        { hu: "Párterápia (90 perc)", en: "Couples therapy (90 min)" },
        { hu: "Családterápia", en: "Family therapy" },
        { hu: "Gyermek-szülő konzultáció", en: "Child-parent consult" },
      ],
      priceHint: {
        hu: "Példa: 20 ülés × 32 000 Ft = 640 000 Ft / hó",
        en: "Example: 20 sessions × 32,000 HUF = 640,000 HUF / month",
      },
    },
    {
      icon: "Briefcase",
      title: { hu: "Specializált programok", en: "Specialized programs" },
      examples: [
        { hu: "10 alkalmas pánikkezelés", en: "10-session panic program" },
        { hu: "Gyász-feldolgozás csomag", en: "Grief processing package" },
        { hu: "Karrier-coaching", en: "Career coaching" },
      ],
      priceHint: {
        hu: "Példa: 10 csomag × 180 000 Ft = 1 800 000 Ft / hó",
        en: "Example: 10 packages × 180,000 HUF = 1,800,000 HUF / month",
      },
    },
    {
      icon: "BookOpen",
      title: { hu: "Digitális anyagok", en: "Digital materials" },
      examples: [
        { hu: "Önismereti munkafüzet", en: "Self-awareness workbook" },
        { hu: "Vezetett meditáció hanganyag", en: "Guided meditation audio" },
        { hu: "Felvett előadás", en: "Recorded lecture" },
      ],
      priceHint: {
        hu: "Passzív bevétel: ~120 000 Ft / hó",
        en: "Passive revenue: ~120,000 HUF / month",
      },
    },
    {
      icon: "Ticket",
      title: { hu: "Tagság és bérletek", en: "Membership and passes" },
      examples: [
        { hu: "Havi 2 ülés tagság", en: "Monthly 2-session membership" },
        { hu: "Vállalati EAP-szerződés", en: "Corporate EAP contract" },
        { hu: "Éves kísérő-program", en: "Annual companion program" },
      ],
      priceHint: {
        hu: "Rendelő-szint: 4 pszichológus × 12 tagsági kliens × 38 000 Ft = 1 824 000 Ft / hó potenciál",
        en: "Practice level: 4 psychologists × 12 member clients × 38,000 HUF = 1,824,000 HUF / month potential",
      },
    },
  ],

  results: [
    {
      value: "+45%",
      label: {
        hu: "csomag-eladás (10 ülés)",
        en: "package sales (10 sessions)",
      },
    },
    { value: "-80%", label: { hu: "adminisztrációs idő", en: "admin time" } },
    {
      value: "100%",
      label: { hu: "GDPR + titkosított adatok", en: "GDPR + encrypted data" },
    },
    { value: "30 perc", label: { hu: "teljes beüzemelés", en: "full setup" } },
  ],
  testimonial: {
    quote: {
      hu: "A kliensek diszkréten foglalnak online, az adataik EU-szerveren titkosítva. Én csak a terápiára koncentrálok: minden adminisztráció automata.",
      en: "Clients book discreetly online, their data encrypted on EU servers. I focus only on therapy: all admin is automated.",
    },
    name: "Dr. Fekete Anna",
    role: { hu: "Klinikai pszichológus", en: "Clinical psychologist" },
    city: { hu: "Budapest, V. kerület", en: "Budapest, District V" },
    avatarInitials: "FA",
  },
  howItWorks: [
    {
      step: 1,
      title: {
        hu: "Chatben elmondod a szolgáltatásaidat",
        en: "Tell us your services in chat",
      },
      body: {
        hu: "Egyéni terápia, párterápia, csoport, online: az AI strukturálja.",
        en: "Individual therapy, couples, group, online: AI structures it.",
      },
    },
    {
      step: 2,
      title: {
        hu: "AI elkészíti a rendelő-oldalt",
        en: "AI builds your practice site",
      },
      body: {
        hu: "Diszkrét profil, kérdőív foglaláskor, csomag-árazás.",
        en: "Discreet profile, intake form at booking, package pricing.",
      },
    },
    {
      step: 3,
      title: {
        hu: "Migráljuk a kliens-adatokat",
        en: "We migrate client data",
      },
      body: {
        hu: "Titkosítva, GDPR-megfelelően: csak te látod.",
        en: "Encrypted, GDPR-compliant: only you can see it.",
      },
    },
    {
      step: 4,
      title: { hu: "Élő, online foglalás", en: "Live, online booking" },
      body: {
        hu: "Diszkrét, automatikus emlékeztető, online ülés-támogatás.",
        en: "Discreet, auto reminders, online session support.",
      },
    },
  ],
  integrations: [
    "Számlázz.hu",
    "Billingo",
    "Google Calendar",
    "Stripe",
    "SimplePay",
    "Google Meet",
    "WhatsApp Business",
  ],
  breadcrumbLabel: {
    hu: "Pszichológusok és mentorok",
    en: "Psychologists & mentors",
  },
  similarPersonas: [
    "therapists-coaches",
    "consultants-business-coaches",
    "nutritionists",
  ],
};
